// Parent email magic-link auth (ARCHITECTURE.md §3). Accounts are parents;
// there is no child-facing surface.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
} from "firebase/auth";
import { auth } from "./firebase";
import { track, EVENTS } from "./analytics";
import { useRef } from "react";

const EMAIL_KEY = "vk_signin_email";

export function actionCodeSettings(): { url: string; handleCodeInApp: true } {
  return { url: window.location.origin + "/signin-complete", handleCodeInApp: true };
}

type AuthState = { loading: boolean; uid: string | null };

const Ctx = createContext<AuthState>({ loading: true, uid: null });
export const useAuth = () => useContext(Ctx);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ loading: true, uid: null });
  const seen = useRef<Set<string>>(new Set());
  useEffect(() => onAuthStateChanged(auth, (u) => {
    // GA4 sign_up: once per uid per session (first auth state we observe).
    if (u && !seen.current.has(u.uid)) {
      seen.current.add(u.uid);
      if (!u.metadata.creationTime ||
          Date.now() - new Date(u.metadata.creationTime).getTime() < 10 * 60 * 1000) {
        track(EVENTS.signUp); // new account only (created within 10 min)
      }
    }
    setState({ loading: false, uid: u?.uid ?? null });
  }), []);
  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export async function requestMagicLink(email: string) {
  await sendSignInLinkToEmail(auth, email, actionCodeSettings());
  localStorage.setItem(EMAIL_KEY, email);
}

export async function completeMagicLinkIfPresent(): Promise<boolean> {
  if (!isSignInWithEmailLink(auth, window.location.href)) return false;
  let email = localStorage.getItem(EMAIL_KEY) ?? "";
  if (!email) email = window.prompt("Please re-enter your email to finish sign-in") ?? "";
  if (!email) return false;
  await signInWithEmailLink(auth, email, window.location.href);
  localStorage.removeItem(EMAIL_KEY);
  return true;
}

export const logout = () => signOut(auth);
